import { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { UserCog, Plus, Loader2, Mail, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const roleConfig = {
    superadmin: { label: 'Super Admin', color: 'bg-amber-500 text-white' },
    support: { label: 'Soporte Tecnico', color: 'bg-blue-500 text-white' },
    account_manager: { label: 'Account Manager', color: 'bg-emerald-500 text-white' },
    billing_admin: { label: 'Finanzas', color: 'bg-violet-500 text-white' },
    developer: { label: 'Developer', color: 'bg-cyan-500 text-white' },
};

export default function AdminStaffPage() {
    const { apiCall } = useAdminAuth();
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ email: '', first_name: '', last_name: '', role: 'support', password: '' });

    const fetchStaff = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiCall('get', '/admin/staff');
            setStaff(res.data);
        } catch { toast.error('Error al cargar equipo'); }
        finally { setLoading(false); }
    }, [apiCall]);

    useEffect(() => { fetchStaff(); }, [fetchStaff]);

    const createStaff = async () => {
        if (!form.email || !form.first_name) { toast.error('Email y nombre requeridos'); return; }
        setSaving(true);
        try {
            await apiCall('post', '/admin/staff', form);
            toast.success('Miembro creado');
            setShowCreate(false);
            setForm({ email: '', first_name: '', last_name: '', role: 'support', password: '' });
            fetchStaff();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
        finally { setSaving(false); }
    };

    const toggleActive = async (s) => {
        try {
            await apiCall('put', `/admin/staff/${s.id}`, { is_active: !s.is_active });
            toast.success(s.is_active ? 'Desactivado' : 'Activado');
            fetchStaff();
        } catch { toast.error('Error'); }
    };

    const deleteStaff = async (s) => {
        try {
            await apiCall('delete', `/admin/staff/${s.id}/permanent`);
            toast.success('Miembro eliminado permanentemente');
            setDeleteConfirm(null);
            fetchStaff();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al eliminar');
        }
    };

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>;

    return (
        <div className="space-y-6" data-testid="admin-staff-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><UserCog className="w-6 h-6 text-amber-500" />Equipo AeroOps</h1>
                    <p className="text-sm text-muted-foreground mt-1">{staff.length} miembros</p>
                </div>
                <Button onClick={() => setShowCreate(true)} data-testid="add-staff-btn"><Plus className="w-4 h-4 mr-1" />Agregar</Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {staff.map(s => {
                    const rc = roleConfig[s.role] || roleConfig.support;
                    return (
                        <Card key={s.id} className={`transition-all ${!s.is_active ? 'opacity-50' : ''}`} data-testid={`staff-${s.id}`}>
                            <CardContent className="p-4">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="font-medium">{s.first_name} {s.last_name}</p>
                                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><Mail className="w-3 h-3" />{s.email}</p>
                                        <Badge className={`${rc.color} text-[10px] mt-2`}>{rc.label}</Badge>
                                    </div>
                                    {s.role !== 'superadmin' && (
                                        <div className="flex flex-col gap-1">
                                            <Button size="sm" variant="ghost" className={`h-7 text-xs ${s.is_active ? 'text-red-500' : 'text-emerald-500'}`} onClick={() => toggleActive(s)} data-testid={`toggle-staff-${s.id}`}>
                                                {s.is_active ? 'Desactivar' : 'Activar'}
                                            </Button>
                                            {!s.is_active && (
                                                <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500" onClick={() => setDeleteConfirm(s)} data-testid={`delete-staff-${s.id}`}>
                                                    <Trash2 className="w-3 h-3 mr-1" />Eliminar
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {/* Create Dialog */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent data-testid="create-staff-dialog">
                    <DialogHeader>
                        <DialogTitle>Nuevo Miembro del Equipo</DialogTitle>
                        <DialogDescription>Se creara con la contrasena temporal indicada</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5"><Label>Nombre *</Label><Input value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} data-testid="staff-firstname" /></div>
                            <div className="space-y-1.5"><Label>Apellido</Label><Input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} data-testid="staff-lastname" /></div>
                        </div>
                        <div className="space-y-1.5"><Label>Email *</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="staff-email" /></div>
                        <div className="space-y-1.5">
                            <Label>Rol</Label>
                            <Select value={form.role} onValueChange={v => setForm({...form, role: v})}>
                                <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="support">Soporte Tecnico</SelectItem>
                                    <SelectItem value="account_manager">Account Manager</SelectItem>
                                    <SelectItem value="billing_admin">Finanzas</SelectItem>
                                    <SelectItem value="developer">Developer</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5"><Label>Contrasena temporal</Label><Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="Mín. 8, mayúscula, número y símbolo" required data-testid="staff-password" /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
                        <Button onClick={createStaff} disabled={saving} data-testid="save-staff-btn">
                            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Crear Miembro
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
                <DialogContent data-testid="delete-staff-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-red-500">Eliminar Miembro</DialogTitle>
                        <DialogDescription>
                            Esta accion es permanente y no se puede deshacer.
                        </DialogDescription>
                    </DialogHeader>
                    {deleteConfirm && (
                        <div className="space-y-3">
                            <div className="p-3 bg-red-500/10 rounded-lg text-sm">
                                <p><strong>{deleteConfirm.first_name} {deleteConfirm.last_name}</strong></p>
                                <p className="text-muted-foreground">{deleteConfirm.email}</p>
                            </div>
                            <p className="text-sm text-muted-foreground">Se eliminara permanentemente este miembro del equipo.</p>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
                        <Button variant="destructive" onClick={() => deleteStaff(deleteConfirm)} data-testid="confirm-delete-staff">
                            <Trash2 className="w-4 h-4 mr-1" />Eliminar Permanentemente
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
